// silks/src/utils/ordersExcel.js
// Download orders as a real Excel (.xlsx) file — no extra npm package needed.
//
//   downloadOrdersExcel(orders, "mana-vastralu-orders.xlsx")
//
// Sheet 1 "Orders" : one row per order (customer, address, amounts, payment, status, tracking)
// Sheet 2 "Items"  : one row per product in each order (name, size, colour, qty, price)
// Everything happens in the browser; nothing is sent anywhere.

const pad = (n) => String(n).padStart(2, "0");
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const txt = (v) => (v === null || v === undefined ? "" : String(v));

export const itemsOf = (o) =>
  Array.isArray(o?.products) && o.products.length ? o.products : Array.isArray(o?.items) ? o.items : [];

function dateParts(d) {
  const t = d ? new Date(d) : null;
  if (!t || isNaN(t)) return ["", ""];
  let h = t.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
  return [`${pad(t.getDate())}-${pad(t.getMonth() + 1)}-${t.getFullYear()}`, `${pad(h)}:${pad(t.getMinutes())} ${ap}`];
}

function addressOf(o) {
  const a = o?.address && typeof o.address === "object" ? o.address : {};
  return {
    name: a.fullName || a.name || o?.userName || "",
    phone: a.phone || (o?.phone && o.phone !== "Not Provided" ? o.phone : ""),
    line: [a.houseNo, a.street || a.address, a.landmark].filter(Boolean).join(", "),
    town: a.village || a.city || "",
    district: a.district || "",
    state: a.state || "",
    pincode: txt(a.pincode),
  };
}

// The rows that go into the file (also used by the tests)
export function buildOrderSheets(orders) {
  const orderHead = ["Order ID", "Short ID", "Date", "Time", "Customer Name", "Email", "Phone", "Address", "Village / City",
    "District", "State", "Pincode", "Products", "Total Qty", "Items Total (₹)", "Shipping (₹)", "Order Total (₹)",
    "Payment Method", "Payment Status", "Payment ID", "Razorpay Order ID", "Order Status", "Courier", "Tracking Number", "Needs Review"];
  const itemHead = ["Order ID", "Short ID", "Date", "Customer Name", "Phone", "Product", "Size", "Colour", "Qty", "Price (₹)", "Line Total (₹)", "Order Status", "Payment Status"];
  const orderRows = [], itemRows = [];
  for (const o of orders || []) {
    if (!o) continue;
    const id = txt(o._id), short = id.slice(-6).toUpperCase();
    const [date, time] = dateParts(o.createdAt || o.created_at);
    const ad = addressOf(o);
    const items = itemsOf(o);
    const itemsTotal = items.reduce((s, i) => s + num(i.price) * Math.max(1, num(i.quantity) || 1), 0);
    const total = num(o.total_amount || o.totalAmount);
    const shipping = total > itemsTotal ? Math.round((total - itemsTotal) * 100) / 100 : 0;
    const qty = items.reduce((s, i) => s + Math.max(1, num(i.quantity) || 1), 0);
    orderRows.push([id, short, date, time, ad.name, txt(o.email), ad.phone, ad.line, ad.town, ad.district, ad.state, ad.pincode,
      items.map((i) => `${txt(i.name)}${i.selectedSize || i.size ? ` (${i.selectedSize || i.size})` : ""} x${Math.max(1, num(i.quantity) || 1)}`).join("; "),
      qty, itemsTotal, shipping, total,
      txt(o.paymentMethod || o.payment_method), txt(o.paymentStatus), txt(o.paymentId), txt(o.razorpay_id),
      txt(o.status), txt(o.courierName), txt(o.trackingNumber), txt(o.needsReview)]);
    for (const i of items) {
      const q = Math.max(1, num(i.quantity) || 1);
      itemRows.push([id, short, date, ad.name, ad.phone, txt(i.name), txt(i.selectedSize || i.size), txt(i.color), q, num(i.price), num(i.price) * q,
        txt(o.status), txt(o.paymentStatus)]);
    }
  }
  return [
    { name: "Orders", head: orderHead, rows: orderRows, widths: [27, 9, 11, 9, 22, 28, 14, 34, 18, 14, 14, 9, 50, 9, 13, 12, 14, 15, 14, 22, 24, 12, 14, 18, 30] },
    { name: "Items", head: itemHead, rows: itemRows, widths: [27, 9, 11, 22, 14, 40, 8, 14, 6, 10, 13, 12, 14] },
  ];
}

// ── minimal .xlsx writer ──────────────────────────────────────────────
const esc = (s) => String(s)
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")      // characters XML can't hold
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const col = (i) => { let s = ""; i += 1; while (i) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); } return s; };

function sheetXml({ head, rows, widths }) {
  const cell = (v, r, c, style) => {
    const ref = `${col(c)}${r}`;
    if (typeof v === "number" && Number.isFinite(v)) return `<c r="${ref}"${style ? ` s="${style}"` : ""}><v>${v}</v></c>`;
    // text is stored as text: phone numbers keep leading zeros, and nothing is run as a formula
    return `<c r="${ref}" t="inlineStr"${style ? ` s="${style}"` : ""}><is><t xml:space="preserve">${esc(v)}</t></is></c>`;
  };
  const all = [head, ...rows];
  const data = all.map((row, ri) => `<row r="${ri + 1}">${row.map((v, ci) => cell(v, ri + 1, ci, ri === 0 ? 1 : 0)).join("")}</row>`).join("");
  const last = `${col(head.length - 1)}${Math.max(1, all.length)}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
    + `<cols>${widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join("")}</cols>`
    + `<sheetData>${data}</sheetData><autoFilter ref="A1:${last}"/></worksheet>`;
}

function workbookFiles(sheets) {
  const files = {
    "[Content_Types].xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")}</Types>`,
    "_rels/.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    "xl/workbook.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${sheets.map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("")}</sheets><definedNames>${sheets.map((s, i) => `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${esc(s.name)}'!$A$1:$${col(s.head.length - 1)}$${Math.max(1, s.rows.length + 1)}</definedName>`).join("")}</definedNames></workbook>`,
    "xl/_rels/workbook.xml.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join("")}<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    "xl/styles.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF8B5E1A"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`,
  };
  sheets.forEach((s, i) => { files[`xl/worksheets/sheet${i + 1}.xml`] = sheetXml(s); });
  return files;
}

// ZIP (stored, no compression) — what an .xlsx file is underneath
const CRC_TABLE = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
const crc32 = (b) => { let c = 0xffffffff; for (let i = 0; i < b.length; i++) c = CRC_TABLE[(c ^ b[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };

function zip(files) {
  const enc = new TextEncoder(), parts = [], central = [];
  let offset = 0;
  const u16 = (v) => [v & 0xff, (v >>> 8) & 0xff], u32 = (v) => [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff];
  for (const [name, content] of Object.entries(files)) {
    const nm = enc.encode(name), data = enc.encode(content), crc = crc32(data);
    const common = [...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0x21), ...u32(crc), ...u32(data.length), ...u32(data.length), ...u16(nm.length), ...u16(0)];
    const local = new Uint8Array([...u32(0x04034b50), ...common]);
    parts.push(local, nm, data);
    central.push(new Uint8Array([...u32(0x02014b50), ...u16(20), ...common, ...u16(0), ...u16(0), ...u16(0), ...u32(0), ...u32(offset)]), nm);
    offset += local.length + nm.length + data.length;
  }
  const cdSize = central.reduce((s, p) => s + p.length, 0), count = Object.keys(files).length;
  const end = new Uint8Array([...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(count), ...u16(count), ...u32(cdSize), ...u32(offset), ...u16(0)]);
  const all = [...parts, ...central, end], out = new Uint8Array(all.reduce((s, p) => s + p.length, 0));
  let p = 0; for (const a of all) { out.set(a, p); p += a.length; }
  return out;
}

export const ordersXlsxBytes = (orders) => zip(workbookFiles(buildOrderSheets(orders)));

export function downloadOrdersExcel(orders, fileName) {
  const d = new Date();
  const name = fileName || `mana-vastralu-orders-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.xlsx`;
  const blob = new Blob([ordersXlsxBytes(orders)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}